import { useMemo } from 'react';
import type { ReportParameterDefinition, ReportParameterValue } from '@/types';
import { ParameterField } from './parameters/ParameterField';
import { useResolvedValues } from './parameters/useResolvedValues';
import '@/styles/parameters.css';

/**
 * The report's own parameters, with the value each one runs with — the thing PBIRS Schedule
 * Manager shows and the portal did not, which made the detail page useless for any report that
 * takes parameters.
 *
 * The control follows what the report declares: a picker where the values are known, a date field
 * for a DateTime, a number field for a number, a checkbox for a Boolean, and free text only where
 * nothing better can be justified.
 *
 * It reads in two columns wherever the card is wide enough, because the report server's own
 * Schedule Manager fits ten parameters in about 200px and this fitted the same ten in 1,191 — one
 * column, with two lines of grey explanation under nearly every field. The explanations are not
 * gone; the repeated ones are. What is left under a control is only what a control cannot show:
 * why a list is empty, why a date is text, that a list on screen is partial, where a query-bound
 * value comes from. The report's own default moved to a named affordance on the label row.
 *
 * Where the allowed values come from a dataset the report evaluates at run time, they are fetched
 * AFTER this component has rendered — see useResolvedValues. That is deliberate: the fetch makes
 * the report server run the report's parameter datasets, measured at 1.2-2.0s on MANDW01, and the
 * detail page is already slow enough. Until it lands (or if it never does) every field behaves
 * exactly as it did before.
 */
export function ParametersEditor({
  definitions,
  values,
  onChange,
  reportPath,
  disabled = false,
  lockedNames
}: {
  definitions: ReportParameterDefinition[];
  values: ReportParameterValue[];
  onChange: (v: ReportParameterValue[]) => void;
  /** The report whose datasets resolve the dynamic value lists. Omit to keep the old free-text behaviour. */
  reportPath?: string;
  disabled?: boolean;
  /**
   * Parameters shown but not editable HERE, because something else owns their value.
   *
   * Distinct from `disabled`, which locks the whole form: this locks named fields inside an
   * otherwise live one. The report pack apply screen uses it for the parameter bound to the head
   * office — that value is written from the customer being applied to, so a field that accepted an
   * edit and then silently discarded it would be worse than one that does not accept it.
   */
  lockedNames?: string[];
}) {
  const { resolved, stateFor, suppliedNames, lookupValues, seededNames } =
    useResolvedValues(reportPath, definitions, values);

  // Matched case-insensitively, like every other parameter-name comparison here: a caller naming
  // the parameter as the pack spells it must lock the field the report spells slightly differently.
  const locked = useMemo(
    () => new Set((lockedNames ?? []).map(n => n.trim().toLowerCase())),
    [lockedNames]);

  // A parameter names its dependencies by parameter name; a reader knows them by their prompt.
  const labelByName = useMemo(() => {
    const map = new Map<string, string>();
    for (const d of definitions) map.set(d.name, d.displayName.trim() === '' ? d.name : d.displayName);
    return map;
  }, [definitions]);

  if (definitions.length === 0) {
    // No definitions but values present means the definitions couldn't be read — the PBIRS
    // fallback carries none. Showing the values read-only beats claiming there are no parameters
    // when the subscription plainly has some.
    return values.length === 0
      ? <div className="placeholder">This report takes no parameters.</div>
      : (
        <>
          <div className="param-note" style={{ marginBottom: 10 }}>
            The report's parameter definitions could not be read, so these are shown as stored and
            cannot be edited here.
          </div>
          <dl className="run-params">
            {values.map(v => (
              <div key={v.name} style={{ display: 'contents' }}>
                <dt>{v.name}</dt>
                <dd>
                  {v.fromField
                    ? <span className="run-param-blank">from the query column {v.fromField}</span>
                    : v.values.length === 0
                      ? <span className="run-param-blank">set by the report</span>
                      : v.values.map((x, i) => (
                        <span key={i} className="run-param-value">{x === '' ? '(blank)' : x}</span>
                      ))}
                </dd>
              </div>
            ))}
          </dl>
        </>
      );
  }

  function current(name: string): ReportParameterValue | undefined {
    return values.find(v => v.name === name);
  }

  /**
   * Replaces one parameter's value IN PLACE.
   *
   * The position matters, which is not obvious. This used to filter the parameter out and append
   * it, so every edit reordered the array — and the resolve query is keyed on a JSON dump of the
   * upstream values. Re-picking a value you already had therefore produced a brand new query key,
   * a cache miss, and another 1.6s round trip that could only return what was already on screen.
   * That is the "it re-does things every time you click" the user reported. The key is sorted
   * defensively as well, but not churning the array is the fix.
   */
  function set(name: string, next: string[]) {
    const entry = { name, values: next, fromField: current(name)?.fromField ?? null };
    const at = values.findIndex(v => v.name === name);
    if (at < 0) return onChange([...values, entry]);

    const copy = values.slice();
    copy[at] = entry;
    onChange(copy);
  }

  return (
    // The shell is what the two-column rule measures. It has to be its own element: a grid cannot
    // answer a container query about itself.
    <div className="param-editor-shell">
      <div className="param-editor">
        {definitions.map(def => {
          const value = current(def.name);
          const seeded = seededNames.get(def.name);
          return (
            <ParameterField
              key={def.name}
              def={def}
              values={value?.values ?? []}
              fromField={value?.fromField ?? null}
              resolved={resolved[def.name]}
              lookupState={stateFor(def)}
              seededWith={seeded}
              /*
               * The update path, decided and deliberate: an existing subscription that stores no
               * identity value is REPAIRED ON REQUEST, never silently on save.
               *
               * The case for repairing it silently is real — such a subscription fails every run,
               * and the person editing it is usually the person it belongs to. The case against
               * won. A save here is triggered by any edit at all, including an admin fixing a typo
               * in somebody else's subject line, and this value decides whose data the report
               * renders: quietly rebinding a working scope to whoever pressed Save is a bigger
               * error than the one it fixes, and it would be invisible in the diff. It is also not
               * urgent to do behind anyone's back, because this failure is loud — every run fails
               * and LastStatus says which parameter — so the repair only has to be available, not
               * automatic. A create is different and does write it: there is no existing scope to
               * change, and the person creating it is by definition the person whose permissions
               * the report will be read with.
               *
               * Offered even where the control itself is disabled because the report does not
               * prompt for this parameter — a report filling this in for itself is exactly the
               * arrangement that resolves to the service account, so "the report handles it" is
               * the fault, not a reason to leave it alone. Never offered for a query-bound value:
               * that belongs to a data-driven subscription's own query and is not ours to set.
               */
              onUseSeededValue={seeded && !value?.fromField && !disabled
                ? () => set(def.name, [seeded])
                : undefined}
              defaultEchoed={suppliedNames.has(def.name)}
              reportPath={reportPath}
              // The SEEDED upstream, not the stored one. A search for a head office has to carry
              // the same User the cascade was resolved with, or the server answers it as the
              // service account and finds nothing.
              upstream={upstreamFor(def, lookupValues)}
              dependencyLabels={unsetDependencies(def, lookupValues).map(n => labelByName.get(n) ?? n)}
              onChange={next => set(def.name, next)}
              // A field-bound parameter belongs to a data-driven subscription's own query. Editing
              // it here would unbind it, so it is shown rather than offered.
              disabled={disabled || locked.has(def.name.toLowerCase())
                || !def.promptUser || Boolean(value?.fromField)}
            />
          );
        })}
      </div>
    </div>
  );
}

/** The values of the parameters one parameter's own dataset takes as arguments. */
function upstreamFor(def: ReportParameterDefinition, values: ReportParameterValue[]): ReportParameterValue[] {
  const names = def.dependencies ?? [];
  return values.filter(v => names.includes(v.name) && v.values.length > 0);
}

/**
 * The dependencies this parameter's dataset needs that nothing has supplied yet.
 *
 * Only these may be named in the "available once X is chosen" note. Naming the whole dependency
 * list regardless is how that note ended up telling users to choose a Head Office and a Country
 * Code that were plainly already set two fields above it.
 */
function unsetDependencies(def: ReportParameterDefinition, supplied: ReportParameterValue[]): string[] {
  const names = new Set(supplied.filter(v => v.values.length > 0).map(v => v.name));
  return (def.dependencies ?? []).filter(name => !names.has(name));
}

/**
 * Says out loud that the subscription about to be created will run as the person creating it.
 *
 * This must not be a hidden side effect, which is why it is here and not only in a code comment:
 * an identity parameter decides whose permissions the report is read with, so it decides whose
 * data lands in the recipients' inboxes. Somebody creating a report for a colleague's clients is
 * entitled to know it will be scoped to their own access and not the colleague's.
 *
 * The long-term cost is stated in the same breath as the benefit, because it is the same
 * sentence's other half and the live estate already demonstrates it: the subscriptions on
 * /Sales/Scope 3 owned by a real person send perfectly well right up until that person leaves.
 *
 * `account` null is not a variant of this message but a refusal — see the caller.
 */
export function RunsAsCreatorNotice({
  account,
  parameterLabels
}: {
  account: string;
  parameterLabels: string[];
}) {
  if (parameterLabels.length === 0) return null;

  return (
    <div className="notice notice-warn" style={{ marginBottom: 14 }}>
      <strong>This subscription will run as you — <code>{account}</code>.</strong>
      <div>
        This report decides what data it can see from its{' '}
        <strong>{parameterLabels.join(', ')}</strong>{' '}
        parameter{parameterLabels.length === 1 ? '' : 's'}, so your account is being saved into
        {parameterLabels.length === 1 ? ' it' : ' them'}. Without that the report server resolves
        {parameterLabels.length === 1 ? ' it' : ' them'} to the portal&rsquo;s own service account,
        which belongs to no client and can see almost nothing — on <code>/Sales/Scope 3</code> that
        is 0 countries where you can see 9 — and the run then fails outright rather than sending a
        smaller report.
        <div style={{ marginTop: 6 }}>
          It stays your account for the life of the subscription: everyone on the recipient list
          receives the data <strong>you</strong> can see, whoever they are, and if you leave Key
          Travel this subscription starts failing until somebody changes it. That is already true
          of the person-owned subscriptions on this report today.
        </div>
      </div>
    </div>
  );
}

/**
 * What may be written into an email subject. Both server placeholders are confirmed present in
 * live subscriptions on this server (154 use the pair together), as is the `@Parameter` form —
 * "/Operations/Daily Bookingsauto" subjects itself with @fromdate and @todate, which are two of
 * that report's own parameters.
 */
export function SubjectPlaceholderHint({ parameterNames }: { parameterNames?: string[] }) {
  return (
    <div className="field-hint">
      Placeholders: <code>@ReportName</code> and <code>@ExecutionTime</code> are filled in by the
      report server when the email is sent. <code>@ParameterName</code> inserts one of the report's
      own parameter values
      {parameterNames && parameterNames.length > 0 && (
        <> — here, {parameterNames.slice(0, 6).map(n => `@${n}`).join(', ')}</>
      )}
      .
    </div>
  );
}
